/**
 * PhotoUpload — reusable component for uploading photos in the public quote form.
 *
 * Features:
 * - Client-side compression via canvas (max 1920px longest side, JPEG 0.82 quality)
 * - Multiple selection, drag-drop, preview thumbnails, per-photo progress
 * - Remove button per photo (deletes locally — backend cleanup by TTL if desired)
 * - Max 12 photos, hard cap. Rejects files > 15 MB pre-compression.
 *
 * Props:
 * - value: string[] — array of uploaded photo IDs
 * - onChange: (nextIds: string[]) => void
 * - maxPhotos?: number
 * - i18n?: { addPhotos, hint, uploading, remove, error }
 */
import { useCallback, useRef, useState } from "react";
import { Camera, Upload, X, ImageIcon, AlertCircle } from "lucide-react";
import { uploadQuotePhoto, quotePhotoUrl } from "../lib/api";

const DEFAULT_I18N = {
  addPhotos: "Ajouter des photos",
  hint: "Formats acceptés : JPG, PNG, HEIC. Max 12 photos. Compressées automatiquement.",
  uploading: "Envoi en cours…",
  remove: "Retirer",
  error: "Impossible d'envoyer cette photo",
};

async function compressImage(file, { maxSide = 1920, quality = 0.82 } = {}) {
  // Return original if not an image or if HEIC (browser can't decode HEIC natively — send raw)
  if (!file.type.startsWith("image/") || file.type === "image/heic" || file.type === "image/heif") return file;
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const { width, height } = bmp;
  const scale = Math.min(1, maxSide / Math.max(width, height));
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  return await new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) { resolve(file); return; }
        // Keep original if compression actually made it bigger
        if (blob.size >= file.size) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.(png|webp|heic|heif)$/i, ".jpg"), { type: "image/jpeg" }));
      },
      "image/jpeg", quality
    );
  });
}

export default function PhotoUpload({ value = [], onChange, maxPhotos = 12, i18n }) {
  const t = { ...DEFAULT_I18N, ...(i18n || {}) };
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState([]); // [{tempId, progress, preview}]
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const remainingSlots = Math.max(0, maxPhotos - value.length - uploading.length);

  const handleFiles = useCallback(async (fileList) => {
    setError("");
    const files = Array.from(fileList || []).slice(0, remainingSlots);
    if (!files.length) return;
    const pending = files.map((f) => ({
      tempId: `tmp_${Math.random().toString(36).slice(2, 9)}`,
      progress: 0,
      preview: URL.createObjectURL(f),
      file: f,
    }));
    setUploading((u) => [...u, ...pending]);
    const results = [];
    for (const item of pending) {
      try {
        if (item.file.size > 15 * 1024 * 1024) throw new Error("Photo trop volumineuse (max 15 Mo)");
        const compressed = await compressImage(item.file);
        const res = await uploadQuotePhoto(compressed, {
          onProgress: (p) => setUploading((u) => u.map((x) => x.tempId === item.tempId ? { ...x, progress: p } : x)),
        });
        results.push(res.id);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || t.error);
      } finally {
        // release object URL
        try { URL.revokeObjectURL(item.preview); } catch { /* ignore */ }
        setUploading((u) => u.filter((x) => x.tempId !== item.tempId));
      }
    }
    if (results.length) onChange([...value, ...results]);
  }, [value, onChange, remainingSlots, t.error]);

  function onDrop(e) {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }
  function remove(id) { onChange(value.filter((x) => x !== id)); }

  return (
    <div className="space-y-3" data-testid="photo-upload">
      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => remainingSlots > 0 && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        className={`relative rounded-2xl border-2 border-dashed transition-all cursor-pointer text-center py-8 px-4
          ${remainingSlots === 0 ? "bg-gray-50 border-gray-200 cursor-not-allowed" : dragOver ? "bg-[#EAF0FF] border-[#2D5BFF]" : "bg-white border-gray-300 hover:border-[#2D5BFF] hover:bg-[#F7F9FF]"}`}
        data-testid="photo-dropzone"
      >
        <input
          ref={inputRef} type="file" accept="image/*" multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="photo-input"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-[#EAF0FF] flex items-center justify-center text-[#2D5BFF]">
            <Camera size={22} />
          </div>
          <div className="font-extrabold text-[#0A0F2C] text-base">{t.addPhotos}</div>
          <div className="text-xs text-[#4B5563] max-w-xs">{t.hint}</div>
          {remainingSlots > 0 && (
            <div className="text-[11px] text-[#9CA3AF] font-semibold">{value.length + uploading.length} / {maxPhotos}</div>
          )}
          {remainingSlots === 0 && <div className="text-[11px] text-red-600 font-bold">Maximum {maxPhotos} photos atteint.</div>}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-red-700 text-xs" data-testid="photo-error">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* Thumbnails */}
      {(value.length > 0 || uploading.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="photo-thumbs">
          {value.map((id) => (
            <div key={id} className="group relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50" data-testid={`photo-thumb-${id}`}>
              <img src={quotePhotoUrl(id)} alt="" loading="lazy" className="w-full h-full object-cover" />
              <button
                type="button" onClick={(e) => { e.stopPropagation(); remove(id); }}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-90 hover:opacity-100 hover:bg-red-600 transition-colors"
                aria-label={t.remove}
                data-testid={`photo-remove-${id}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {uploading.map((u) => (
            <div key={u.tempId} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
              <img src={u.preview} alt="" className="w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 text-white text-[10px] font-bold">
                <Upload size={16} className="mb-1 animate-pulse" />
                {u.progress}%
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
